import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Download, AlertCircle, CheckCircle2, FileJson, Loader2, RefreshCw, Search, FolderSync, Circle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const NUTRITION_LABELS = {
  energy_kj: "Énergie (kJ)",
  energy_kcal: "Énergie (kcal)",
  fat: "Matières grasses (g)",
  saturated_fat: "  dont saturées (g)",
  carbs: "Glucides (g)",
  sugars: "  dont sucres (g)",
  proteins: "Protéines (g)",
  salt: "Sel (g)",
};

const fmtVal = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const eq = (a, b) => {
  const na = a === null || a === undefined || a === "" ? null : Number(a);
  const nb = b === null || b === undefined || b === "" ? null : Number(b);
  if (na === null && nb === null) return true;
  if (na === null || nb === null) return false;
  return Math.abs(na - nb) < 0.01;
};

export default function NutritionImporter({ onImported }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("nutrical"); // "nutrical" | "paste"
  const [step, setStep] = useState("paste"); // paste | preview
  const [raw, setRaw] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Cases cochées pour les champs à appliquer
  const [applyName, setApplyName] = useState(true);
  const [applyIngredients, setApplyIngredients] = useState(true);
  const [applyAllergens, setApplyAllergens] = useState(true);
  const [applyNutritionKeys, setApplyNutritionKeys] = useState(new Set(Object.keys(NUTRITION_LABELS)));

  // --- État "Pull depuis NutriCal" ---
  const [nutricalList, setNutricalList] = useState([]);
  const [nutricalLoading, setNutricalLoading] = useState(false);
  const [nutricalError, setNutricalError] = useState(null);
  const [nutricalSearch, setNutricalSearch] = useState("");
  const [nutricalSelected, setNutricalSelected] = useState(new Set());
  const [batchResult, setBatchResult] = useState(null);

  const reset = () => {
    setMode("nutrical");
    setStep("paste");
    setRaw("");
    setPreviewData(null);
    setApplyName(true);
    setApplyIngredients(true);
    setApplyAllergens(true);
    setApplyNutritionKeys(new Set(Object.keys(NUTRITION_LABELS)));
    setNutricalSearch("");
    setNutricalSelected(new Set());
    setBatchResult(null);
  };

  const handleOpen = (v) => {
    setOpen(v);
    if (v) {
      // Lance le fetch NutriCal dès l'ouverture
      loadNutrical();
    } else {
      reset();
    }
  };

  const loadNutrical = async () => {
    setNutricalLoading(true);
    setNutricalError(null);
    try {
      const { data } = await api.get("/nutrical/recipes");
      setNutricalList(data || []);
    } catch (e) {
      setNutricalError(e?.response?.data?.detail || "NutriCal injoignable");
      setNutricalList([]);
    } finally {
      setNutricalLoading(false);
    }
  };

  const filteredNutrical = useMemo(() => {
    const q = nutricalSearch.trim().toLowerCase();
    if (!q) return nutricalList;
    return nutricalList.filter((r) =>
      (r.recipe_name || "").toLowerCase().includes(q) ||
      (r.sku || "").toLowerCase().includes(q)
    );
  }, [nutricalList, nutricalSearch]);

  const toggleRecipe = (id) => {
    const next = new Set(nutricalSelected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setNutricalSelected(next);
  };

  const toggleSelectAllFiltered = () => {
    const allIds = filteredNutrical.map((r) => r.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => nutricalSelected.has(id));
    const next = new Set(nutricalSelected);
    if (allSelected) {
      allIds.forEach((id) => next.delete(id));
    } else {
      allIds.forEach((id) => next.add(id));
    }
    setNutricalSelected(next);
  };

  const importNutricalBatch = async () => {
    if (nutricalSelected.size === 0) {
      toast.error("Sélectionnez au moins une recette");
      return;
    }
    setLoading(true);
    setBatchResult(null);
    try {
      const { data } = await api.post("/nutrical/import-batch", {
        recipe_ids: Array.from(nutricalSelected),
      });
      setBatchResult(data);
      const total = (data.created?.length || 0) + (data.updated?.length || 0);
      if (total > 0) {
        toast.success(`✅ Import terminé : ${data.created.length} créés, ${data.updated.length} mis à jour`);
        if (onImported) onImported();
      } else if ((data.errors?.length || 0) > 0) {
        toast.error(`Aucune recette importée — ${data.errors.length} erreur(s)`);
      } else {
        toast.warning("Rien à importer (recettes ignorées)");
      }
      setNutricalSelected(new Set());
      // Recharge la liste pour refresh des statuts
      loadNutrical();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'import batch");
    } finally {
      setLoading(false);
    }
  };
  // --- Fin état NutriCal ---

  const doPreview = async () => {
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      toast.error("JSON invalide : " + e.message);
      return;
    }
    if (!parsed.sku) {
      toast.error("Le champ 'sku' est obligatoire dans le JSON");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/products/import-nutrition/preview", parsed);
      setPreviewData(data);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'analyse");
    } finally {
      setLoading(false);
    }
  };

  const toggleNutritionKey = (k) => {
    const next = new Set(applyNutritionKeys);
    if (next.has(k)) next.delete(k); else next.add(k);
    setApplyNutritionKeys(next);
  };

  const doConfirm = async () => {
    if (!previewData) return;
    const incoming = previewData.incoming;
    const payload = {
      sku: previewData.sku,
      create_if_missing: previewData.action === "create",
    };
    if (previewData.action === "create" || applyName) payload.name = incoming.name;
    if (applyIngredients) payload.ingredients = incoming.ingredients || "";
    if (applyAllergens) payload.allergens = incoming.allergens || [];
    if (applyNutritionKeys.size > 0) {
      const nut = {};
      for (const k of applyNutritionKeys) {
        nut[k] = incoming.nutrition?.[k] ?? null;
      }
      payload.nutrition = nut;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/products/import-nutrition/confirm", payload);
      toast.success(previewData.action === "create" ? `Produit ${data.sku} créé` : `Produit ${data.sku} mis à jour`);
      handleOpen(false);
      if (onImported) onImported(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const action = previewData?.action;
  const existing = previewData?.existing || {};
  const incoming = previewData?.incoming || {};

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="import-nutrition-btn">
          <FileJson className="w-4 h-4 mr-2" /> Importer depuis food-calc-14
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E5E5E0]">
          <DialogTitle className="font-heading flex items-center gap-2">
            <Download className="w-5 h-5 text-[#1F4E3D]" />
            Importer les valeurs nutritionnelles
          </DialogTitle>
          <DialogDescription>
            Synchronisez vos produits avec les recettes calculées dans <strong>NutriCal</strong> (food-calc-14), ou collez un JSON manuellement.
          </DialogDescription>
        </DialogHeader>

        {/* Onglets */}
        {step === "paste" && (
          <div className="px-6 pt-3 pb-0 border-b border-[#E5E5E0] flex gap-1">
            <button
              onClick={() => setMode("nutrical")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                mode === "nutrical"
                  ? "border-[#1F4E3D] text-[#1F4E3D]"
                  : "border-transparent text-[#575A57] hover:text-[#1A1C1A]"
              }`}
              data-testid="tab-nutrical"
            >
              <FolderSync className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Récupérer depuis NutriCal
            </button>
            <button
              onClick={() => setMode("paste")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                mode === "paste"
                  ? "border-[#1F4E3D] text-[#1F4E3D]"
                  : "border-transparent text-[#575A57] hover:text-[#1A1C1A]"
              }`}
              data-testid="tab-paste"
            >
              <FileJson className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Coller un JSON
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === "paste" && mode === "nutrical" && (
            <NutricalPanel
              loading={nutricalLoading}
              error={nutricalError}
              list={filteredNutrical}
              totalCount={nutricalList.length}
              search={nutricalSearch}
              onSearch={setNutricalSearch}
              selected={nutricalSelected}
              onToggle={toggleRecipe}
              onToggleAll={toggleSelectAllFiltered}
              onReload={loadNutrical}
              batchResult={batchResult}
            />
          )}
          {step === "paste" && mode === "paste" && (
            <div className="space-y-3">
              <Textarea
                placeholder={`Exemple :
{
  "schema_version": "1.0",
  "source": "food-calc-14",
  "sku": "SKU-K6",
  "name": "Crevettes au curry vert, riz thaï",
  "ingredients": "Riz thaï parfumé, poulet (28%)...",
  "allergens": ["Gluten", "Soja"],
  "nutrition": {
    "energy_kj": 723, "energy_kcal": 172,
    "fat": 7.8, "saturated_fat": 3.2,
    "carbs": 18.4, "sugars": 2.1,
    "proteins": 7.5, "salt": 0.6
  },
  "nutrition_basis": "per_100g"
}`}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                rows={16}
                className="font-mono text-xs"
                data-testid="import-nutrition-textarea"
              />
              <div className="text-xs text-[#575A57] bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3">
                <strong>💡 Astuce :</strong> dans NutriCal, cliquez sur « Copier pour AgroManage » puis collez ici (Ctrl+V). Les valeurs <code>per_portion</code> sont converties automatiquement en <code>per_100g</code> si <code>net_weight</code> est fourni.
              </div>
            </div>
          )}

          {step === "preview" && previewData && (
            <div className="space-y-4">
              <div className={`rounded-lg p-3 flex items-center gap-2 ${action === "create" ? "bg-[#E5EFF8] border border-[#C5D8E8]" : "bg-[#FBF4E9] border border-[#E8D3A8]"}`}>
                {action === "create" ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-[#2D5A87]" />
                    <div>
                      <div className="font-semibold text-[#1A1C1A]">Nouveau produit à créer</div>
                      <div className="text-xs text-[#575A57]">SKU <code className="font-mono">{previewData.sku}</code> n'existe pas. Il sera créé avec les valeurs ci-dessous.</div>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-[#8C6B2F]" />
                    <div>
                      <div className="font-semibold text-[#1A1C1A]">Produit existant trouvé : {existing.name}</div>
                      <div className="text-xs text-[#575A57]">SKU <code className="font-mono">{previewData.sku}</code>. Cochez les champs à mettre à jour. Les valeurs non cochées resteront inchangées.</div>
                    </div>
                  </>
                )}
              </div>

              {/* Tableau diff */}
              <div className="border border-[#E5E5E0] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9F9F6] text-[10px] uppercase tracking-wider text-[#575A57]">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold w-10">✓</th>
                      <th className="text-left px-3 py-2 font-semibold">Champ</th>
                      {action === "update" && <th className="text-left px-3 py-2 font-semibold">Actuel</th>}
                      <th className="text-left px-3 py-2 font-semibold">Entrant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Nom — éditable uniquement en update */}
                    {action === "update" && (
                      <DiffRow
                        checked={applyName}
                        onCheck={() => setApplyName(!applyName)}
                        label="Nom"
                        current={existing.name || "—"}
                        incoming={incoming.name || "—"}
                        same={existing.name === incoming.name}
                        testid="diff-name"
                      />
                    )}
                    <DiffRow
                      checked={applyIngredients}
                      onCheck={() => setApplyIngredients(!applyIngredients)}
                      label="Ingrédients"
                      current={action === "update" ? (existing.ingredients || "—") : null}
                      incoming={incoming.ingredients || "—"}
                      same={action === "update" && existing.ingredients === incoming.ingredients}
                      showCurrent={action === "update"}
                      multiline
                      testid="diff-ingredients"
                    />
                    <DiffRow
                      checked={applyAllergens}
                      onCheck={() => setApplyAllergens(!applyAllergens)}
                      label="Allergènes"
                      current={action === "update" ? ((existing.allergens || []).join(", ") || "—") : null}
                      incoming={
                        <div className="flex flex-wrap gap-1">
                          {(incoming.allergens || []).length === 0 ? (
                            <span>—</span>
                          ) : (
                            incoming.allergens.map((a, i) => (
                              <Badge key={i} variant="outline" className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]">{a}</Badge>
                            ))
                          )}
                        </div>
                      }
                      same={action === "update" && JSON.stringify(existing.allergens || []) === JSON.stringify(incoming.allergens || [])}
                      showCurrent={action === "update"}
                      testid="diff-allergens"
                    />
                    {/* Nutrition : une ligne par champ */}
                    {Object.keys(NUTRITION_LABELS).map((k) => {
                      const curV = existing.nutrition?.[k];
                      const inV = incoming.nutrition?.[k];
                      const same = action === "update" && eq(curV, inV);
                      return (
                        <DiffRow
                          key={k}
                          checked={applyNutritionKeys.has(k)}
                          onCheck={() => toggleNutritionKey(k)}
                          label={NUTRITION_LABELS[k]}
                          current={action === "update" ? fmtVal(curV) : null}
                          incoming={fmtVal(inV)}
                          same={same}
                          showCurrent={action === "update"}
                          testid={`diff-nutrition-${k}`}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="text-xs text-[#575A57]">
                <strong>💡</strong> Les lignes en <span className="text-[#1F4E3D] font-medium">vert</span> sont identiques (rien à changer). Les autres sont les <em>vraies</em> mises à jour.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-[#E5E5E0]">
          {step === "paste" && mode === "nutrical" ? (
            <>
              <Button variant="outline" onClick={() => handleOpen(false)} disabled={loading}>Fermer</Button>
              <Button
                onClick={importNutricalBatch}
                disabled={loading || nutricalSelected.size === 0}
                className="bg-[#1F4E3D] hover:bg-[#173B2E]"
                data-testid="nutrical-import-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Importer {nutricalSelected.size > 0 ? `${nutricalSelected.size} recette${nutricalSelected.size > 1 ? "s" : ""}` : ""}
              </Button>
            </>
          ) : step === "paste" ? (
            <>
              <Button variant="outline" onClick={() => handleOpen(false)} disabled={loading}>Annuler</Button>
              <Button onClick={doPreview} disabled={loading || !raw.trim()} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="import-preview-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Analyser le JSON
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep("paste")} disabled={loading}>← Retour</Button>
              <Button onClick={doConfirm} disabled={loading} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="import-confirm-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {action === "create" ? "Créer le produit" : "Appliquer les modifications"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiffRow({ checked, onCheck, label, current, incoming, same, showCurrent = true, multiline = false, testid }) {
  return (
    <tr className={`border-t border-[#F0F0EB] ${same ? "bg-[#F4FAF5]" : ""}`} data-testid={testid}>
      <td className="px-3 py-2 align-top">
        <Checkbox checked={checked} onCheckedChange={onCheck} disabled={same} />
      </td>
      <td className="px-3 py-2 align-top font-medium text-[#1A1C1A] text-xs">
        {label}
        {same && <span className="ml-1 text-[10px] text-[#1F4E3D] font-normal">(identique)</span>}
      </td>
      {showCurrent && (
        <td className={`px-3 py-2 align-top text-xs text-[#575A57] ${multiline ? "" : "truncate max-w-[200px]"}`}>
          {current}
        </td>
      )}
      <td className={`px-3 py-2 align-top text-xs ${same ? "text-[#575A57]" : "text-[#1A1C1A] font-medium"}`}>
        {incoming}
      </td>
    </tr>
  );
}


function NutricalPanel({ loading, error, list, totalCount, search, onSearch, selected, onToggle, onToggleAll, onReload, batchResult }) {
  const allFilteredIds = list.map((r) => r.id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));

  return (
    <div className="space-y-3">
      {/* Récap du dernier batch */}
      {batchResult && (
        <div className="bg-[#EAF4EE] border border-[#C5DBCF] rounded-lg p-3 text-sm" data-testid="batch-result">
          <div className="font-semibold text-[#1F4E3D] mb-1 flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" /> Dernier import terminé
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div><span className="text-[#1F4E3D] font-bold">{batchResult.created?.length || 0}</span> créé(s)</div>
            <div><span className="text-[#2D5A87] font-bold">{batchResult.updated?.length || 0}</span> mis à jour</div>
            <div><span className="text-[#A3A6A3] font-bold">{batchResult.skipped?.length || 0}</span> ignoré(s)</div>
            <div><span className="text-[#C05C47] font-bold">{batchResult.errors?.length || 0}</span> erreur(s)</div>
          </div>
          {batchResult.errors?.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-[#C05C47] font-medium">Voir les erreurs</summary>
              <ul className="mt-1 space-y-0.5 pl-3">
                {batchResult.errors.map((e, i) => <li key={i} className="text-[#575A57]">• {e.recipe_id} → {e.error}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Barre de recherche + Reload */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#A3A6A3]" />
          <Input
            placeholder="Rechercher par nom ou SKU…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
            data-testid="nutrical-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onReload} disabled={loading} data-testid="nutrical-reload">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* État */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#575A57] py-4 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement depuis NutriCal…
        </div>
      )}
      {!loading && error && (
        <div className="bg-[#FCEEEA] border border-[#E8C1B4] rounded p-3 text-sm text-[#C05C47]" data-testid="nutrical-error">
          <strong>NutriCal injoignable :</strong> {error}
        </div>
      )}
      {!loading && !error && totalCount === 0 && (
        <div className="text-sm text-[#A3A6A3] italic border border-dashed border-[#E5E5E0] rounded p-6 text-center">
          Aucune recette disponible dans NutriCal. Demandez à la cuisine de créer + associer un SKU.
        </div>
      )}
      {!loading && !error && totalCount > 0 && list.length === 0 && (
        <div className="text-sm text-[#A3A6A3] italic border border-dashed border-[#E5E5E0] rounded p-4 text-center">
          Aucune recette ne correspond à « {search} ».
        </div>
      )}

      {/* Tableau */}
      {!loading && !error && list.length > 0 && (
        <div className="border border-[#E5E5E0] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F6] text-[10px] uppercase tracking-wider text-[#575A57]">
              <tr>
                <th className="px-3 py-2 text-left w-10">
                  <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="nutrical-select-all" />
                </th>
                <th className="px-3 py-2 text-left">Recette NutriCal</th>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-right">Énergie / 100g</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const isSelected = selected.has(r.id);
                const isSynced = r.sku_status === "synced";
                return (
                  <tr
                    key={r.id}
                    className={`border-t border-[#F0F0EB] hover:bg-[#FAFAF7] cursor-pointer ${isSelected ? "bg-[#F4FAF5]" : ""}`}
                    onClick={() => onToggle(r.id)}
                    data-testid={`nutrical-row-${r.id}`}
                  >
                    <td className="px-3 py-2.5">
                      <Checkbox checked={isSelected} onCheckedChange={() => onToggle(r.id)} onClick={(e) => e.stopPropagation()} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="text-sm font-medium text-[#1A1C1A]">{r.recipe_name}</div>
                      {isSynced && r.local_product_name && r.local_product_name !== r.recipe_name && (
                        <div className="text-[10px] text-[#A3A6A3] italic">→ {r.local_product_name} (nom local conservé)</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs">{r.sku}</td>
                    <td className="px-3 py-2.5">
                      {isSynced ? (
                        <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#C5DBCF] text-[10px]" data-testid="status-synced">
                          <Circle className="w-2 h-2 mr-1 fill-current" /> Synchronisée
                        </Badge>
                      ) : (
                        <Badge className="bg-[#E5EFF8] text-[#2D5A87] border-[#C5D8E8] text-[10px]" data-testid="status-to-create">
                          <Circle className="w-2 h-2 mr-1 fill-current" /> À créer
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-[#575A57]">
                      {r.energy_kcal_per_100g != null ? `${r.energy_kcal_per_100g.toFixed(0)} kcal` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && list.length > 0 && (
        <div className="text-xs text-[#575A57]">
          <strong>💡</strong> {list.length} recette{list.length > 1 ? "s" : ""} affichée{list.length > 1 ? "s" : ""}
          {totalCount !== list.length && ` (sur ${totalCount} au total)`} ·
          🟢 <strong>Synchronisée</strong> : SKU déjà dans AgroManage (sera mis à jour) ·
          🔵 <strong>À créer</strong> : nouveau produit ·
          <em> Le nom local n'est jamais écrasé.</em>
        </div>
      )}
    </div>
  );
}
